import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { formatINR } from '../../utils/currency';
import './Wishlist.css';

const Wishlist = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [buyingNow, setBuyingNow] = useState({});

  useEffect(() => {
    if (user) {
      fetchWishlist();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchWishlist = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/ecommerce/wishlist');
      if (response.data.success) {
        setWishlistItems(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching wishlist:', error);
      setError('Failed to load wishlist');
    } finally {
      setLoading(false);
    }
  };

  const removeFromWishlist = async (productId) => {
    try {
      const response = await api.delete(`/ecommerce/wishlist/remove/${productId}`);
      if (response.data.success) {
        setWishlistItems(wishlistItems.filter(item => item.product._id !== productId));
      }
    } catch (error) {
      console.error('Error removing from wishlist:', error);
    }
  };

  const addToCart = async (productId, inventoryId) => {
    try {
      const response = await api.post('/api/ecommerce/cart/add', {
        productId,
        inventoryId,
        quantity: 1
      });
      if (response.data.success) {
        alert('Item added to cart!');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Failed to add item to cart');
    }
  };

  const buyNow = async (productId) => {
    try {
      setBuyingNow(prev => ({ ...prev, [productId]: true }));
      const response = await api.post('/api/ecommerce/buy-now', {
        productId,
        quantity: 1
      });
      
      if (response.data.success) {
        // Navigate to checkout page
        navigate('/ecommerce/checkout');
      }
    } catch (error) {
      console.error('Error processing buy now:', error);
      alert(error.response?.data?.message || 'Error processing buy now');
    } finally {
      setBuyingNow(prev => ({ ...prev, [productId]: false }));
    }
  };

  if (!user) {
    return (
      <div className="wishlist-container">
        <div className="wishlist-empty">
          <h2>Please log in to view your wishlist</h2>
          <Link to="/login" className="btn btn-primary">Login</Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="wishlist-container">
        <div className="wishlist-loading">
          <h2>Loading your wishlist...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="wishlist-container">
        <div className="wishlist-error">
          <h2>Error loading wishlist</h2>
          <p>{error}</p>
          <button onClick={fetchWishlist} className="btn btn-primary">Try Again</button>
        </div>
      </div>
    );
  }

  if (wishlistItems.length === 0) {
    return (
      <div className="wishlist-container">
        <div className="wishlist-empty">
          <h2>Your wishlist is empty</h2>
          <p>Start adding items you love to your wishlist!</p>
          <Link to="/ecommerce" className="btn btn-primary">Continue Shopping</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="wishlist-container">
      <div className="wishlist-header">
        <h1>My Wishlist</h1>
        <p>{wishlistItems.length} item{wishlistItems.length !== 1 ? 's' : ''}</p>
      </div>

      <div className="wishlist-grid">
        {wishlistItems.map((item) => (
          <div key={item._id} className="wishlist-item">
            <div className="item-image">
              <Link to={`/ecommerce/products/${item.product.slug}`}>
                {item.product.inventory?.images && item.product.inventory.images.length > 0 ? (
                  <img
                    src={item.product.inventory.images[0]}
                    alt={item.product.inventory.itemName}
                    onError={(e) => {
                      e.target.src = '/placeholder-product.jpg';
                    }}
                  />
                ) : (
                  <div className="placeholder-image">
                    <span>No Image</span>
                  </div>
                )}
              </Link>
            </div>
            
            <div className="item-details">
              <h3>
                <Link to={`/ecommerce/products/${item.product.slug}`}>
                  {item.product.inventory?.itemName}
                </Link>
              </h3>
              <p className="item-code">SKU: {item.product.inventory?.itemCode}</p>
              
              {item.product.variants && item.product.variants.length > 0 && (
                <div className="item-variants">
                  {item.product.variants.map((variant, index) => (
                    <span key={index} className="variant-tag">
                      {variant.name}: {variant.value}
                    </span>
                  ))}
                </div>
              )}
              
              <div className="item-price">
                <span className="current-price">{formatINR(item.product.inventory?.discountedPrice || item.product.inventory?.sellingPrice)}</span>
                {item.product.inventory?.discountedPrice && item.product.inventory?.discountedPrice < item.product.inventory?.sellingPrice && (
                  <span className="original-price">{formatINR(item.product.inventory?.sellingPrice)}</span>
                )}
              </div>
              
              <div className="item-actions">
                <button 
                  className="btn btn-buy-now"
                  onClick={() => buyNow(item.product._id)}
                  disabled={buyingNow[item.product._id] || item.product.inventory?.itemQuantity <= 0}
                >
                  {buyingNow[item.product._id] ? 'Processing...' : 'Buy Now'}
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={() => addToCart(item.product._id, item.product.inventory?._id)}
                >
                  Add to Cart
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={() => removeFromWishlist(item.product._id)}
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Wishlist;
